import cn from 'classnames'
import { JSX, memo, useCallback, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'
import { ICheckedValue } from 'core-components/library/list/types'
import { MultiSelect, MultiSelectDynamic } from 'core-components/library/multiSelect'
import { Tooltip } from 'core-components/library/tooltip'

import { ValidationType } from '../../configs'
import { useFormField } from '../../hooks/useFormField'
import { ReadOnlyField } from '../readOnlyField'

import { TFormMultiselectProps } from './models'

import styles from './FormMultiselect.module.scss'

export const FormMultiselect = memo(
  <T,>({
    field,
    formKey,
    label = '',
    defaultValue,
    source,
    caption,
    onChange,
    onValidate,
    isDisabled,
    isRequired,
    isReadOnly,
    isStatic,
    isVisible = true,
    isSelectAllAllow,
    validationType = ValidationType.ONCHANGE,
    classes,
    margin,
    placeholder,
    ...rest
  }: TFormMultiselectProps<T>) => {
    const { handleChange, value, validationMessage, isModified } = useFormField<string[]>({
      field,
      formKey,
      defaultValue,
      onChange,
      onValidate,
      isDisabled,
      isRequired,
      isReadOnly,
      isVisible,
      validationType,
    })
    const translationMode = useSelector(selectTranslationMode)
    const isTranslationMode = translationMode === 'edit'
    const t = useSafetyTranslation()

    const translatedPlaceholder = placeholder ? t(placeholder) : undefined

    const Component = useMemo(() => (isStatic ? MultiSelect : MultiSelectDynamic), [isStatic])

    const checkedValues = useMemo<ICheckedValue[]>(
      () => value.map((val) => ({ key: val, value: 'full' })),
      [value]
    )

    const handleCreate = useCallback(
      (itemLabel: string) => {
        handleChange([...value, itemLabel])
      },
      [handleChange, value]
    )

    const handleCheck = useCallback(
      (item: T, checked: boolean) => {
        const itemValue = (item as unknown as Record<string, any>)[caption || 'value']
        if (checked) {
          handleChange([...value, itemValue])
        } else {
          handleChange(value.filter((val) => val !== itemValue))
        }
      },
      [caption, handleChange, value]
    )

    if (isVisible) {
      if (isReadOnly) {
        return <ReadOnlyField label={label} value={value} />
      }

      return (
        <div className={styles.validationContainer}>
          <Tooltip disabled={!validationMessage} tipContent={() => <>{validationMessage}</>}>
            <RenderWhen condition={isTranslationMode && Boolean(label)}>
              <Translation text={label} span />
            </RenderWhen>
            <Component
              classes={{
                ...classes,
                controlClasses: {
                  ...classes?.controlClasses,
                  root: cn(classes?.controlClasses?.root, {
                    [styles.required]: isRequired && Boolean(label),
                    [styles.modified]: isModified,
                    [styles.invalid]: validationMessage,
                  }),
                  popPaper: cn(classes?.controlClasses?.popPaper),
                },
              }}
              label={label ? t(label) : undefined}
              value={value}
              onChange={handleChange}
              onCreate={handleCreate}
              onCheck={handleCheck}
              checkedValues={checkedValues}
              source={source}
              caption={caption}
              disabled={isDisabled}
              allowSelectAll={isSelectAllAllow}
              margin={margin}
              placeholder={translatedPlaceholder}
              {...rest}
            />
          </Tooltip>
        </div>
      )
    }

    return null
  },
  shallowEqual
) as <T>(props: TFormMultiselectProps<T>) => JSX.Element
